import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { producaoApi, errorMessage, type ProducaoConfig } from "./api"
import { useProducao } from "./context"
import { selectClass } from "./AssignPanel"

export type TrelloOption = { id: string; name: string }

async function loadBoard(shell: NonNullable<ReturnType<typeof useProducao>["shell"]>, boardId: string) {
  const [ls, lbs, mbs] = await Promise.all([
    producaoApi.trello(shell, "lists", boardId),
    producaoApi.trello(shell, "labels", boardId),
    producaoApi.trello(shell, "members", boardId),
  ])
  return {
    lists: ls.map((l) => ({ id: String(l.id), name: String(l.name) })),
    labels: lbs.map((l) => ({ id: String(l.id), name: `${l.name || "(sem nome)"}${l.color ? ` · ${l.color}` : ""}` })),
    members: mbs.map((m) => ({ id: String(m.id), name: String(m.full_name || m.username) })),
  }
}

/**
 * Etiquetas e membros do quadro configurado, para escolher a etiqueta de cada
 * carrinha e o membro de cada técnico (Produção → Equipas). Sem quadro, vazio.
 */
export function useTrelloOptions() {
  const { shell } = useProducao()
  const [options, setOptions] = useState<{ labels: TrelloOption[]; members: TrelloOption[] }>({ labels: [], members: [] })
  useEffect(() => {
    if (!shell) return
    let cancelled = false
    producaoApi
      .config(shell)
      .then((cfg) => (cfg.trello.board_id ? loadBoard(shell, cfg.trello.board_id) : null))
      .then((b) => !cancelled && b && setOptions({ labels: b.labels, members: b.members }))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [shell])
  return options
}

/**
 * Produção → Configurações · sincronização com o Trello e limiar de
 * sobrecarga. Qualquer pessoa da Produção vê; só administradores gravam.
 */
export function TrelloConfig() {
  const { shell, reload } = useProducao()
  const canEdit = !!shell?.isAdmin
  const [cfg, setCfg] = useState<ProducaoConfig | null>(null)
  const [lists, setLists] = useState<TrelloOption[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!shell) return
    producaoApi.config(shell).then(setCfg).catch((err) => setLoadError(errorMessage(err)))
  }, [shell])

  const boardId = cfg?.trello.board_id ?? ""
  useEffect(() => {
    if (!shell || !boardId) return
    let cancelled = false
    loadBoard(shell, boardId)
      .then((b) => {
        if (cancelled) return
        setLists(b.lists)
        setLoadError(null)
      })
      .catch((err) => !cancelled && setLoadError(errorMessage(err)))
    return () => {
      cancelled = true
    }
  }, [shell, boardId])

  if (!cfg) return null
  const setTrelloField = (k: keyof ProducaoConfig["trello"], v: string) => setCfg({ ...cfg, trello: { ...cfg.trello, [k]: v || null } })

  const save = async () => {
    if (!shell) return
    setSaving(true)
    try {
      setCfg(await producaoApi.saveConfig(shell, { trello: cfg.trello, overload_threshold: cfg.overload_threshold }))
      shell.bridge.toast({ variant: "success", title: "Configuração guardada" })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Erro ao guardar", description: errorMessage(err) })
    } finally {
      setSaving(false)
    }
  }

  const listSelect = (k: "list_planned_id" | "list_in_progress_id" | "list_done_id", label: string) => (
    <Field label={label} htmlFor={`tr-${k}`}>
      {lists.length > 0 ? (
        <select id={`tr-${k}`} className={selectClass} value={cfg.trello[k] ?? ""} onChange={(e) => setTrelloField(k, e.target.value)}>
          <option value="">—</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      ) : (
        <Input id={`tr-${k}`} value={cfg.trello[k] ?? ""} onChange={(e) => setTrelloField(k, e.target.value)} placeholder="Id da lista" />
      )}
    </Field>
  )

  return (
    <section className="space-y-3 rounded-xl border p-4 border-border">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Planeamento e Trello</h2>
          <p className="text-xs text-muted-foreground">Só do Koortex para o Trello: o que se muda à mão no Trello é sobreposto na sincronização seguinte.</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
          </Button>
        )}
      </div>
      {!cfg.trello_configured && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          Por configurar. As atribuições gravam na mesma e ficam «Trello por sincronizar» até haver quadro e lista das planeadas.
        </p>
      )}
      <fieldset disabled={!canEdit} className="grid min-w-0 gap-3 md:grid-cols-2">
        <Field label="Quadro (id ou shortLink do URL)" htmlFor="tr-board">
          <Input id="tr-board" value={cfg.trello.board_id ?? ""} onChange={(e) => setTrelloField("board_id", e.target.value)} placeholder="ex.: aBc12DeF" />
        </Field>
        <Field label="Aviso de sobrecarga a partir da obra n.º" htmlFor="tr-thr">
          <Input id="tr-thr" type="number" min={2} max={20} value={cfg.overload_threshold} onChange={(e) => setCfg({ ...cfg, overload_threshold: Number(e.target.value) || 4 })} />
        </Field>
        {listSelect("list_planned_id", "Lista das planeadas")}
        {listSelect("list_in_progress_id", "Lista «em execução»")}
        {listSelect("list_done_id", "Lista das concluídas")}
      </fieldset>
      {loadError && boardId && <p className="text-sm text-destructive">Trello: {loadError}</p>}
    </section>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}
